// IMPORTANT: This file mirrors server/src/Share/AGWA.Security/AgwaSecurityCatalog.cs.
// Any changes here MUST be reflected in the C# version and vice versa.
//
// The path this header carried until 2026-08-29 - AGWA.Modules.Core/Claims/Content/Id.cs - does not
// exist and had not for some time, which is why the sync it demands was never performed: D-067
// renamed a domain server-side and nothing pointed the next reader at the file to follow it into.
// A wrong path in a rule is worse than no rule, because it reads as though it were being obeyed.

// Well-known IDs - these are seeded/hardcoded values that never change
export const WellKnownIds = {
  /** The AGWA system tenant group ID - seeded in DefaultSeedData.cs */
  agwaGroupId: 'a9c7b891-35d5-4c4f-b688-1a61c4109e4c',
} as const;

// Claim IDs
export const ClaimId = {
  userId: 'user.id',
  userUsername: 'user.username',
  userEmailAddress: 'user.emailaddress',
  userGroupId: 'user.groupId',
  groupId: 'group.id',
  groupName: 'group.name',
  groupActive: 'group.active',
} as const;
export type ClaimId = (typeof ClaimId)[keyof typeof ClaimId];

// Domain IDs - These MUST match server AgwaSecurityCatalog.Domains, and a `.node.spec.ts` now
// asserts it against security-catalog.yml rather than trusting this comment. See
// ui/src/pages/app-members/shared/organization-type-labels.node.spec.ts.
export const DomainId = {
  systemTenant: 'AGWA.SystemTenant',
  glassProcessor: 'Prime.Glass.Manufacturer',
  glassDataEntry: 'Glass.DataEntry',
  glassAuditor: 'Glass.Auditor',
  // RENAMED from `productRegistry: 'Product.Registry'` by D-067, which the UI never followed. The
  // stale code denied every Product Registry route to the companies that hold the domain
  // (route-access.ts matches the code exactly) and threw on eight member screens.
  primeSystemSupplier: 'Prime.System.Supplier',
  // The hidden operation type that owns the Product Registry (D-127). Mirrors
  // AgwaSecurityCatalog.Domains.PrimeSystemIntellectualProperty and sits beside the supplier for
  // the same reason it does there: the two are read together wherever a register is resolved.
  primeSystemIntellectualProperty: 'Prime.System.IntellectualProperty',
  productDataEntry: 'Product.DataEntry',
  productAuditor: 'Product.Auditor',
  // The operation types with no module behind them yet. Seeded since D-058 and absent here, which
  // is the other half of the same gap: the server issued codes the UI had never heard of.
  primeSystemManufacturer: 'Prime.System.Manufacturer',
  fabricator: 'Fabricator',
  reseller: 'Reseller',
  installer: 'Installer',
  primeGlassSupplier: 'Prime.Glass.Supplier',
  glassWholesaler: 'Glass.Wholesaler',
  glazier: 'Glazier',
} as const;
export type DomainId = (typeof DomainId)[keyof typeof DomainId];

// System Role IDs - These MUST match server AgwaSecurityCatalog.Roles
export const SystemRoleId = {
  memberAdmin: 'AGWA.Core.MemberAdmin',
  userAdmin: 'AGWA.Core.UserAdmin',
  staffAdmin: 'AGWA.Core.StaffAdmin',
} as const;
export type SystemRoleId = (typeof SystemRoleId)[keyof typeof SystemRoleId];

// Member Role IDs
export const MemberRoleId = {
  memberTenant: 'membertenant',
} as const;
export type MemberRoleId = (typeof MemberRoleId)[keyof typeof MemberRoleId];

// Common Role IDs - These MUST match server AgwaSecurityCatalog.Roles
export const CommonRoleId = {
  admin: 'AGWA.Core.Admin',
  user: 'user',
} as const;
export type CommonRoleId = (typeof CommonRoleId)[keyof typeof CommonRoleId];

// Feature IDs
export const FeatureId = {
  moduleGdc: 'module.gdc',
} as const;
export type FeatureId = (typeof FeatureId)[keyof typeof FeatureId];

// GDC Roles - These MUST match server AgwaSecurityCatalog.Roles.GDC*
export const GdcRoles = {
  Admin: 'GDC.Admin',
  Creator: 'GDC.Creator',
  Approver: 'GDC.Approver',
  Consumer: 'GDC.Consumer',
} as const;
export type GdcRoles = (typeof GdcRoles)[keyof typeof GdcRoles];

// Glass Accreditation Roles - These MUST match server AgwaSecurityCatalog.Roles.GlassAccreditation*
export const GlassAccreditationRoles = {
  Creator: 'Glass.Accreditation.Creator',
  Auditor: 'Glass.Accreditation.Auditor',
  Consumer: 'Glass.Accreditation.Consumer',
} as const;
export type GlassAccreditationRoles =
  (typeof GlassAccreditationRoles)[keyof typeof GlassAccreditationRoles];

// Product Registry Roles - These MUST match server AgwaSecurityCatalog.Roles.ProductRegistry*
export const ProductRegistryRoles = {
  Creator: 'Product.Registry.Creator',
  Auditor: 'Product.Registry.Auditor',
  SupportAdmin: 'Product.Registry.SupportAdmin',
} as const;
export type ProductRegistryRoles =
  (typeof ProductRegistryRoles)[keyof typeof ProductRegistryRoles];

// Workflow Sandbox Roles - These MUST match server AgwaSecurityCatalog.Roles.WorkflowSandbox*
export const WorkflowSandboxRoles = {
  Creator: 'Workflow.Sandbox.Creator',
} as const;
export type WorkflowSandboxRoles =
  (typeof WorkflowSandboxRoles)[keyof typeof WorkflowSandboxRoles];

// Experimental Roles - These MUST match server AgwaSecurityCatalog.Roles.Experimental*
export const ExperimentalRoles = {
  Access: 'Experimental.Access',
} as const;
export type ExperimentalRoles =
  (typeof ExperimentalRoles)[keyof typeof ExperimentalRoles];

// Group Type Claim IDs
export const GroupTypeClaimId = {
  groupTypeInternal: 'group.type.internal',
  groupTypeManufacturer: 'group.type.manufacturer',
  groupTypeFabricator: 'group.type.fabricator',
  groupTypeSimulator: 'group.type.simulator',
  groupTypeAuditor: 'group.type.auditor',
} as const;
export type GroupTypeClaimId =
  (typeof GroupTypeClaimId)[keyof typeof GroupTypeClaimId];
